import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Progress } from "../../components/ui/progress";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Upload, Phone, Play, Pause, X, Trash2, Users, TrendingUp,
  Clock, CheckCircle, AlertCircle, PhoneOff, Loader2, ShieldAlert, PhoneMissed
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminVoiceCampaigns({ authToken }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [dncList, setDncList] = useState([]);
  const [dncOpen, setDncOpen] = useState(false);

  const headers = { Authorization: `Bearer ${authToken}` };

  const loadCampaigns = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/voice-campaigns`, { headers });
      setCampaigns(r.data.campaigns || []);
    } catch (e) {
      toast.error("Kampagnen konnten nicht geladen werden");
    }
  }, [authToken]);

  const loadDnc = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/do-not-call`, { headers });
      setDncList(r.data.entries || []);
    } catch (e) { /* ignore */ }
  }, [authToken]);

  useEffect(() => {
    loadCampaigns();
    loadDnc();
    const t = setInterval(loadCampaigns, 10000);
    return () => clearInterval(t);
  }, [loadCampaigns, loadDnc]);

  const changeStatus = async (id, action) => {
    try {
      await axios.post(`${API}/admin/voice-campaigns/${id}/${action}`, {}, { headers });
      toast.success(`Kampagne ${action === "start" ? "gestartet" : action === "pause" ? "pausiert" : "abgebrochen"}`);
      loadCampaigns();
    } catch (e) { toast.error("Aktion fehlgeschlagen"); }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Kampagne wirklich löschen? Alle zugehörigen Leads werden entfernt.")) return;
    try {
      await axios.delete(`${API}/admin/voice-campaigns/${id}`, { headers });
      toast.success("Kampagne gelöscht");
      loadCampaigns();
    } catch (e) { toast.error("Löschen fehlgeschlagen"); }
  };

  const statusColor = {
    draft: "bg-slate-100 text-slate-700",
    running: "bg-emerald-100 text-emerald-700",
    paused: "bg-amber-100 text-amber-700",
    completed: "bg-sky-100 text-sky-700",
    cancelled: "bg-rose-100 text-rose-700",
  };

  const statusLabel = {
    draft: "Entwurf",
    running: "Läuft",
    paused: "Pausiert",
    completed: "Abgeschlossen",
    cancelled: "Abgebrochen",
  };

  return (
    <div className="space-y-6" data-testid="voice-campaigns-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Voice-Kampagnen</h1>
          <p className="text-sm text-slate-500 mt-1">
            Automatisierte Outbound-Anrufe mit KI Lea an Facebook/Instagram-Leads
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => { setDncOpen(true); loadDnc(); }}
            data-testid="dnc-button"
          >
            <ShieldAlert className="h-4 w-4 mr-2" />
            Do-Not-Call ({dncList.length})
          </Button>
          <Button
            onClick={() => setUploadOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="new-campaign-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            Neue Kampagne
          </Button>
        </div>
      </header>

      {campaigns.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Phone className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-2">Noch keine Voice-Kampagnen vorhanden</p>
            <p className="text-xs text-slate-400 mb-4">Lade eine Excel/CSV-Datei mit Facebook-Leads hoch, um zu starten.</p>
            <Button onClick={() => setUploadOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="empty-state-new-campaign">
              <Upload className="h-4 w-4 mr-2" /> Neue Kampagne erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4" data-testid="campaigns-list">
          {campaigns.map((c) => {
            const stats = c.stats || {};
            const done = (stats.done || 0) + (stats.blocked || 0) + (stats.failed || 0);
            const percent = c.total_leads ? Math.round((done * 100) / c.total_leads) : 0;
            return (
              <Card key={c.id} className="hover:shadow-md transition-shadow" data-testid={`campaign-${c.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-base">{c.name}</CardTitle>
                        <Badge className={`${statusColor[c.status] || ""} border-0`}>
                          {statusLabel[c.status] || c.status}
                        </Badge>
                        <Badge variant="outline" className="text-[10px]">
                          <Users className="h-3 w-3 mr-1" /> {c.total_leads} Leads
                        </Badge>
                      </div>
                      {c.description && (
                        <CardDescription className="mt-1">{c.description}</CardDescription>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {c.status === "running" ? (
                        <Button size="sm" variant="outline" onClick={() => changeStatus(c.id, "pause")} data-testid={`pause-${c.id}`}>
                          <Pause className="h-4 w-4" />
                        </Button>
                      ) : c.status === "paused" || c.status === "draft" ? (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => changeStatus(c.id, "start")}
                          data-testid={`start-${c.id}`}
                        >
                          <Play className="h-4 w-4 mr-1" /> Start
                        </Button>
                      ) : null}
                      {(c.status === "running" || c.status === "paused") && (
                        <Button size="sm" variant="outline" onClick={() => changeStatus(c.id, "cancel")} data-testid={`cancel-${c.id}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setSelectedCampaign(c)} data-testid={`details-${c.id}`}>
                        Details
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deleteCampaign(c.id)} data-testid={`delete-${c.id}`}>
                        <Trash2 className="h-4 w-4 text-rose-500" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-3 text-xs">
                    <StatChip icon={Clock} label="Ausstehend" value={stats.pending || 0} color="text-slate-600" />
                    <StatChip icon={Loader2} label="Laufend" value={stats.in_progress || 0} color="text-blue-600" />
                    <StatChip icon={CheckCircle} label="Erledigt" value={stats.done || 0} color="text-emerald-600" />
                    <StatChip icon={PhoneMissed} label="Voicemail" value={stats.voicemail || 0} color="text-amber-600" />
                    <StatChip icon={PhoneOff} label="Besetzt" value={stats.busy || 0} color="text-orange-600" />
                    <StatChip icon={AlertCircle} label="Blockiert" value={stats.blocked || 0} color="text-rose-600" />
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={percent} className="flex-1 h-2" />
                    <span className="text-xs text-slate-500">{done}/{c.total_leads} · {percent}%</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-2">
                    Zeitfenster: Mo-Sa {c.schedule?.start_hour || 9}:00 - {c.schedule?.end_hour || 20}:00 · Max {c.schedule?.max_calls_per_day || 120}/Tag
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {uploadOpen && (
        <UploadDialog
          authToken={authToken}
          onClose={() => setUploadOpen(false)}
          onCreated={() => { setUploadOpen(false); loadCampaigns(); }}
        />
      )}
      {selectedCampaign && (
        <CampaignDetailDialog
          campaign={selectedCampaign}
          authToken={authToken}
          onClose={() => setSelectedCampaign(null)}
        />
      )}
      {dncOpen && (
        <DncDialog
          entries={dncList}
          authToken={authToken}
          onClose={() => setDncOpen(false)}
          onChange={loadDnc}
        />
      )}
    </div>
  );
}

function StatChip({ icon: Icon, label, value, color }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className={`h-3.5 w-3.5 ${color || ""}`} />
      <div>
        <div className="text-xs font-semibold">{value}</div>
        <div className="text-[10px] text-slate-500">{label}</div>
      </div>
    </div>
  );
}

function UploadDialog({ authToken, onClose, onCreated }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileB64, setFileB64] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [maxPerDay, setMaxPerDay] = useState(120);
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(20);
  const headers = { Authorization: `Bearer ${authToken}` };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    if (!name) setName(f.name.replace(/\.(xlsx|xls|csv)$/i, ""));
    setLoading(true);
    // upload for preview
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await axios.post(`${API}/admin/voice-campaigns/preview`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      // also convert to base64 for reuse
      const b64 = await new Promise((resolve) => {
        const rd = new FileReader();
        rd.onload = () => resolve(rd.result.split(",")[1]);
        rd.readAsDataURL(f);
      });
      setFileB64(b64);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!fileB64 || !preview || preview.valid_leads === 0) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/voice-campaigns`, {
        name: name || "Neue Kampagne",
        description,
        file_content_b64: fileB64,
        schedule: {
          weekdays: [0, 1, 2, 3, 4, 5],
          start_hour: startHour,
          end_hour: endHour,
          max_calls_per_day: maxPerDay,
        },
      }, { headers });
      toast.success("Kampagne erstellt");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erstellen fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle>Neue Voice-Kampagne</DialogTitle>
          <DialogDescription>
            Lade eine Excel-Datei mit Facebook/Instagram-Leads hoch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Excel/CSV-Datei</Label>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFile}
              className="block w-full text-sm border rounded-md p-2 mt-1"
              data-testid="file-input"
            />
          </div>

          {loading && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Datei wird analysiert...
            </div>
          )}

          {preview && (
            <Alert className={preview.valid_leads > 0 ? "" : "border-rose-300 bg-rose-50"}>
              <AlertDescription>
                <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                  <div>✅ Gültige Leads: <strong>{preview.valid_leads}</strong></div>
                  <div>⚠️ Ungültig: <strong>{preview.invalid_rows}</strong></div>
                  <div>🔁 Duplikate in Datei: <strong>{preview.duplicates_within_file}</strong></div>
                  <div>🚫 Auf DNC-Liste: <strong>{preview.duplicates_with_dnc}</strong></div>
                </div>
                {preview.warnings?.length > 0 && (
                  <details className="text-xs text-slate-600 mt-2">
                    <summary className="cursor-pointer">Warnungen ({preview.warnings.length})</summary>
                    <ul className="mt-1 list-disc pl-5 space-y-0.5">
                      {preview.warnings.map((w, i) => (<li key={i}>{w}</li>))}
                    </ul>
                  </details>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div>
            <Label htmlFor="camp-name">Kampagnen-Name</Label>
            <Input id="camp-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Facebook Januar 2026" data-testid="campaign-name-input" />
          </div>
          <div>
            <Label htmlFor="camp-desc">Beschreibung (optional)</Label>
            <Input id="camp-desc" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Notizen zur Kampagne" data-testid="campaign-desc-input" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Start (Uhr)</Label>
              <Input type="number" min={0} max={23} value={startHour} onChange={(e) => setStartHour(parseInt(e.target.value) || 9)} data-testid="start-hour" />
            </div>
            <div>
              <Label>Ende (Uhr)</Label>
              <Input type="number" min={0} max={23} value={endHour} onChange={(e) => setEndHour(parseInt(e.target.value) || 20)} data-testid="end-hour" />
            </div>
            <div>
              <Label>Max/Tag</Label>
              <Input type="number" min={1} max={500} value={maxPerDay} onChange={(e) => setMaxPerDay(parseInt(e.target.value) || 120)} data-testid="max-per-day" />
            </div>
          </div>
          <p className="text-[11px] text-slate-500">
            Anrufe erfolgen nur Mo-Sa im gewählten Zeitfenster. Sonntags niemals.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={submit}
            disabled={!preview || preview.valid_leads === 0 || loading}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="submit-campaign"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TrendingUp className="h-4 w-4 mr-2" />}
            Kampagne erstellen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CampaignDetailDialog({ campaign, authToken, onClose }) {
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("");
  const headers = { Authorization: `Bearer ${authToken}` };

  const load = useCallback(async () => {
    try {
      const url = `${API}/admin/voice-campaigns/${campaign.id}/leads${filter ? `?status=${filter}` : ""}`;
      const r = await axios.get(url, { headers });
      setLeads(r.data.leads || []);
    } catch (e) { /* ignore */ }
  }, [campaign.id, filter, authToken]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const statusColor = {
    pending: "bg-slate-100 text-slate-700",
    in_progress: "bg-blue-100 text-blue-700",
    done: "bg-emerald-100 text-emerald-700",
    voicemail: "bg-amber-100 text-amber-700",
    busy: "bg-orange-100 text-orange-700",
    no_answer: "bg-orange-100 text-orange-700",
    failed: "bg-rose-100 text-rose-700",
    blocked: "bg-rose-100 text-rose-700",
    revoked: "bg-rose-100 text-rose-700",
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]" data-testid="campaign-detail">
        <DialogHeader>
          <DialogTitle>{campaign.name}</DialogTitle>
          <DialogDescription>
            {campaign.total_leads} Leads · Status: <strong>{campaign.status}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 flex-wrap mb-3">
          {["", "pending", "in_progress", "done", "voicemail", "blocked", "failed"].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={filter === s ? "default" : "outline"}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s || 'all'}`}
            >
              {s === "" ? "Alle" : s}
            </Button>
          ))}
        </div>

        <ScrollArea className="h-[500px] pr-2">
          <div className="space-y-2">
            {leads.map((l) => (
              <div
                key={l.lead_id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 gap-2"
                data-testid={`lead-${l.lead_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">
                    {l.first_name} {l.last_name} · <span className="text-slate-500 font-mono">{l.phone}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 flex gap-2 flex-wrap">
                    {l.platform && <span>📱 {l.platform}</span>}
                    {l.email && <span>✉️ {l.email}</span>}
                    {l.attempts > 0 && <span>🔁 {l.attempts} Versuche</span>}
                    {l.last_attempt_at && <span>⏱ {l.last_attempt_at.slice(0, 16).replace("T", " ")}</span>}
                  </div>
                  {l.result_summary && (
                    <div className="text-[11px] text-slate-600 mt-1 italic">{l.result_summary}</div>
                  )}
                </div>
                <Badge className={`${statusColor[l.status] || ""} border-0 shrink-0`}>
                  {l.status}
                </Badge>
              </div>
            ))}
            {leads.length === 0 && (
              <div className="text-center text-slate-500 py-6 text-sm">Keine Leads mit diesem Filter</div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function DncDialog({ entries, authToken, onClose, onChange }) {
  const [newPhone, setNewPhone] = useState("");
  const [reason, setReason] = useState("");
  const headers = { Authorization: `Bearer ${authToken}` };

  const add = async () => {
    if (!newPhone) return;
    try {
      await axios.post(`${API}/admin/do-not-call`, { phone: newPhone, reason: reason || "admin_manual" }, { headers });
      setNewPhone(""); setReason("");
      toast.success("Blockiert");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  const remove = async (phone) => {
    if (!window.confirm(`${phone} wieder freigeben?`)) return;
    try {
      await axios.delete(`${API}/admin/do-not-call/${encodeURIComponent(phone)}`, { headers });
      toast.success("Wieder freigegeben");
      onChange();
    } catch (e) { toast.error("Fehlgeschlagen"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="dnc-dialog">
        <DialogHeader>
          <DialogTitle>Do-Not-Call Liste</DialogTitle>
          <DialogDescription>
            Diese Telefonnummern werden nie angerufen - egal in welcher Kampagne.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label>Neue Nummer sperren</Label>
            <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="+491701234567" data-testid="dnc-new-phone" />
          </div>
          <div className="flex-1">
            <Label>Grund</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="z.B. Kein Interesse mehr" data-testid="dnc-reason" />
          </div>
          <Button onClick={add} data-testid="dnc-add">Sperren</Button>
        </div>

        <ScrollArea className="h-80 mt-3">
          <div className="space-y-1">
            {entries.map((e) => (
              <div key={e.phone} className="flex items-center justify-between border rounded p-2 text-sm">
                <div>
                  <div className="font-mono">{e.phone}</div>
                  <div className="text-[11px] text-slate-500">
                    {e.reason || "kein Grund"} · {e.source} · {(e.revoked_at || "").slice(0, 16).replace("T", " ")}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => remove(e.phone)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
            {entries.length === 0 && (
              <div className="text-slate-500 text-center py-6 text-sm">Liste leer</div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
